import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react"
import { TinyColor } from "@ctrl/tinycolor"
import { IconBoltLightningOutline18 as Bolt } from "nucleo-ui-outline-18"

import type {
	QuickSuggestion,
	QuickSuggestionLabel,
	QuickSuggestionMember,
	QuickSuggestionProject,
} from "@productlane/lib/src/quick-suggestions"
import { cn } from "@productlane/lib/src/cn"
import { quickSuggestions } from "@productlane/lib/src/quick-suggestions"
import { Avatar, Button } from "@productlane/ui"

import { LinearPriorityIcon } from "@/constants"
import { getProjectIcon } from "@/lib/projects"

// Enter: 4px up with a fade, 200ms strong ease-out; opacity only under reduced motion.
const ENTER_CLASSES =
	"transition-[opacity,transform] duration-200 ease-out-quint [@starting-style]:translate-y-1 [@starting-style]:opacity-0 motion-reduce:[@starting-style]:translate-y-0"
const STAGGER_MS = 40

interface Label extends QuickSuggestionLabel {
	color: string
}

interface Project extends QuickSuggestionProject {
	icon?: string | null
	color?: string | null
}

interface Member extends QuickSuggestionMember {
	avatarUrl?: string | null
}

interface Priority {
	priority: number
	label: string
}

function keyOf(suggestion: QuickSuggestion): string {
	switch (suggestion.kind) {
		case "label":
			return `label:${suggestion.label.id}`
		case "project":
			return `project:${suggestion.project.id}`
		case "priority":
			return `priority:${suggestion.priority}`
		case "assignee":
			return `assignee:${suggestion.member.id}`
	}
}

// textContent runs paragraphs together ("appIt is slow"), which breaks whole-word matching.
function htmlToText(html: string): string {
	if (!html) return ""
	const spaced = html.replace(
		/<\/(p|div|li|h[1-6]|blockquote|pre)>|<br\s*\/?>/gi,
		"$&\n",
	)
	return (
		new DOMParser().parseFromString(spaced, "text/html").body.textContent ?? ""
	)
}

/**
 * Labels, a project, a priority and teammates the title and description name, one click to apply,
 * like Linear's quick suggestions. Keyword matching only, so it keeps up with
 * typing.
 */
export function CreateIssueQuickSuggestions({
	isOpen,
	title,
	descriptionHtml,
	labels,
	projects,
	teamId,
	selectedLabelIds,
	selectedProjectId,
	priorities = [],
	selectedPriority,
	members = [],
	selectedAssigneeId,
	onAddLabel,
	onSetProject,
	onSetPriority,
	onSetAssignee,
}: {
	isOpen: boolean
	title: string
	descriptionHtml: string
	labels: readonly Label[]
	projects: readonly Project[]
	teamId: string
	selectedLabelIds: readonly string[]
	selectedProjectId?: string | null
	priorities?: readonly Priority[]
	selectedPriority?: number | null
	members?: readonly Member[]
	selectedAssigneeId?: string | null
	onAddLabel: (labelId: string) => void
	onSetProject: (projectId: string) => void
	onSetPriority?: (priority: number) => void
	onSetAssignee?: (memberId: string) => void
}) {
	const priorityValues = useMemo(
		() => priorities.map(({ priority }) => priority),
		[priorities],
	)
	const deferredTitle = useDeferredValue(title)
	const deferredHtml = useDeferredValue(descriptionHtml)
	const text = useMemo(
		() => `${deferredTitle}\n${htmlToText(deferredHtml)}`,
		[deferredTitle, deferredHtml],
	)
	const suggestions = useMemo(
		() =>
			quickSuggestions({
				text,
				labels,
				projects,
				teamId,
				selectedLabelIds,
				selectedProjectId,
				priorities: onSetPriority ? priorityValues : [],
				selectedPriority,
				members: onSetAssignee ? members : [],
				selectedAssigneeId,
			}),
		[
			text,
			labels,
			projects,
			teamId,
			selectedLabelIds,
			selectedProjectId,
			priorityValues,
			selectedPriority,
			members,
			selectedAssigneeId,
			onSetPriority,
			onSetAssignee,
		],
	)

	// Once the row has shown, it keeps its height for the rest of the draft, so
	// the property buttons below do not jump up and down while someone types.
	const [hasShown, setHasShown] = useState(false)
	useEffect(() => {
		if (suggestions.length > 0) setHasShown(true)
	}, [suggestions.length])
	useEffect(() => {
		if (!isOpen) setHasShown(false)
	}, [isOpen])

	// Stagger only the chips of a reveal; one that turns up mid-sentence shows at once.
	const revealDelays = useRef(new Map<string, number>())
	if (suggestions.length === 0) revealDelays.current = new Map()
	else if (revealDelays.current.size === 0) {
		suggestions.forEach((suggestion, index) =>
			revealDelays.current.set(keyOf(suggestion), (index + 1) * STAGGER_MS),
		)
	}
	const enterDelay = (suggestion: (typeof suggestions)[number]) => {
		const delay = revealDelays.current.get(keyOf(suggestion))
		return delay ? { transitionDelay: `${delay}ms` } : undefined
	}

	if (!hasShown && suggestions.length === 0) return null

	return (
		<div
			data-testid="quick-suggestions"
			className="flex min-h-7 flex-wrap items-center gap-2"
		>
			{suggestions.length > 0 && (
				<span
					className={cn(
						"mr-1 flex items-center gap-1.5 text-xs font-medium text-secondary",
						ENTER_CLASSES,
					)}
				>
					<Bolt className="h-4 w-4 flex-none text-tertiary" />
					Quick suggestions
				</span>
			)}
			{suggestions.map((suggestion) => {
				if (suggestion.kind === "label") {
					const { label } = suggestion
					return (
						<Button
							key={keyOf(suggestion)}
							variant="suggested"
							className={ENTER_CLASSES}
							style={enterDelay(suggestion)}
							onClick={() => onAddLabel(label.id)}
						>
							<span
								className="mr-1.5 h-[9px] w-[9px] flex-none rounded-full"
								style={{
									backgroundColor: new TinyColor(label.color)
										.setAlpha(0.8)
										.toString(),
								}}
							/>
							{label.name}
						</Button>
					)
				}
				if (suggestion.kind === "priority") {
					const { priority } = suggestion
					return (
						<Button
							key={keyOf(suggestion)}
							variant="suggested"
							Icon={LinearPriorityIcon[priority]}
							className={ENTER_CLASSES}
							style={enterDelay(suggestion)}
							onClick={() => onSetPriority?.(priority)}
						>
							{priorities.find((p) => p.priority === priority)?.label}
						</Button>
					)
				}
				if (suggestion.kind === "assignee") {
					const { member } = suggestion
					return (
						<Button
							key={keyOf(suggestion)}
							variant="suggested"
							className={ENTER_CLASSES}
							style={enterDelay(suggestion)}
							onClick={() => onSetAssignee?.(member.id)}
						>
							<Avatar
								name={member.name}
								image={member.avatarUrl}
								size="xxs"
								className="mr-1.5"
							/>
							{member.name}
						</Button>
					)
				}
				const { project } = suggestion
				return (
					<Button
						key={keyOf(suggestion)}
						variant="suggested"
						Icon={getProjectIcon(project.icon)}
						iconStyle={{ color: project.color ?? undefined }}
						className={ENTER_CLASSES}
						style={enterDelay(suggestion)}
						onClick={() => onSetProject(project.id)}
					>
						{project.name}
					</Button>
				)
			})}
		</div>
	)
}
